import { useWidgetConfig } from '@brandclub/bc-widgets';
import React__default, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useDomainConfig, useSuggestedQueries } from '../api/cache/appConfigQueryOptions.js';
import { usePageContext } from './hooks/usePageContext.js';
import './styles/animations.js';

const DEFAULT_ROTATION_INTERVAL_MS = 4000;
const INITIAL_APPEARANCE_DELAY_MS = 2500; // Progressive disclosure delay
const DISMISSED_STORAGE_KEY = 'concierge_suggested_pills_dismissed';
const getPillPosition = (position, buttonSize, horizontalOffset, verticalOffset) => {
    const gap = 12; // Space between pill and button
    const styles = {
        position: 'fixed',
    };
    switch (position) {
        case 'bottom-right':
            return Object.assign(Object.assign({}, styles), { bottom: verticalOffset + buttonSize + gap, right: horizontalOffset });
        case 'bottom-left':
            return Object.assign(Object.assign({}, styles), { bottom: verticalOffset + buttonSize + gap, left: horizontalOffset });
        case 'top-right':
            return Object.assign(Object.assign({}, styles), { top: verticalOffset + buttonSize + gap, right: horizontalOffset });
        case 'top-left':
            return Object.assign(Object.assign({}, styles), { top: verticalOffset + buttonSize + gap, left: horizontalOffset });
        default:
            return Object.assign(Object.assign({}, styles), { bottom: verticalOffset + buttonSize + gap, right: horizontalOffset });
    }
};
const SuggestedMessagePills = ({ onPillClick, position, buttonSize, horizontalOffset, verticalOffset, }) => {
    const { data: domainConfig } = useDomainConfig();
    const brandId = domainConfig === null || domainConfig === void 0 ? void 0 : domainConfig.brandId;
    // Page context for contextual relevance (#3)
    const pageContext = usePageContext();
    // Get theme editor overrides
    const widgetConfig = useWidgetConfig('floating-button', {
        position: 'bottom-right',
        horizontalOffset: 20,
        verticalOffset: 20,
        size: 44,
        backgroundColor: '#1e293b',
        backgroundColorHover: '#334155',
        iconColor: '#ffffff',
        borderRadius: 50,
        shadowColor: '#181d56',
        shadowOpacity: 0.25,
        shadowBlur: 50,
        shadowSpread: -12,
        shadowOffsetY: 25,
        iconSize: 22,
        hoverScale: 1.1,
        animationDuration: 200,
        showSuggestedMessages: true,
        suggestedMessages: undefined,
        suggestedMessageRotationMs: 4000,
        // Pill styling
        pillBackgroundColor: '#ffffff',
        pillTextColor: '#1f2937',
        pillBorderRadius: 12,
        pillFontSize: 14,
        pillPaddingX: 16,
        pillPaddingY: 10,
        pillShadowOpacity: 0.12,
        pillsToShow: 2,
        // Header styling
        pillHeaderText: undefined,
        pillHeaderColor: '#6b7280',
        pillHeaderFontSize: 11,
    });
    // Fetch suggested queries from API
    const { data: suggestedQueriesResponse } = useSuggestedQueries(brandId);
    // State for progressive disclosure (#1)
    const [isVisible, setIsVisible] = useState(false);
    // State for pill rotation
    const [startIndex, setStartIndex] = useState(0);
    const [animationClass, setAnimationClass] = useState('');
    // State for hover pause (#5)
    const [isHovered, setIsHovered] = useState(false);
    const rotationTimerRef = useRef(null);
    // State for user dismissal (persisted in localStorage)
    const [isDismissed, setIsDismissed] = useState(() => {
        if (typeof window === 'undefined')
            return false;
        return localStorage.getItem(DISMISSED_STORAGE_KEY) === 'true';
    });
    const handleDismiss = useCallback(() => {
        setIsDismissed(true);
        if (typeof window !== 'undefined') {
            localStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
        }
    }, []);
    // Determine messages source with memoization
    const messages = useMemo(() => {
        var _a;
        const themeMessages = widgetConfig.suggestedMessages;
        const apiMessages = ((_a = suggestedQueriesResponse === null || suggestedQueriesResponse === void 0 ? void 0 : suggestedQueriesResponse.suggestedQuestions) === null || _a === void 0 ? void 0 : _a.map((q) => q.query)) || [];
        return themeMessages && themeMessages.length > 0 ? themeMessages : apiMessages;
    }, [widgetConfig.suggestedMessages, suggestedQueriesResponse === null || suggestedQueriesResponse === void 0 ? void 0 : suggestedQueriesResponse.suggestedQuestions]);
    // Rotation interval
    const rotationIntervalMs = widgetConfig.suggestedMessageRotationMs || DEFAULT_ROTATION_INTERVAL_MS;
    // Check if pills should be shown
    const showPills = widgetConfig.showSuggestedMessages !== false;
    // Number of pills to show (from theme editor)
    const pillsToShow = widgetConfig.pillsToShow || 2;
    // Get contextual header text based on page type, with theme editor override
    const headerText = useMemo(() => {
        // Theme editor override takes precedence
        if (widgetConfig.pillHeaderText) {
            return widgetConfig.pillHeaderText;
        }
        // Otherwise use contextual text based on page type
        switch (pageContext.pageType) {
            case 'pdp':
                return 'Ask about this product';
            case 'brand':
            case 'brandclub':
                return 'Ask about this brand';
            default:
                return 'Ask about';
        }
    }, [pageContext.pageType, widgetConfig.pillHeaderText]);
    // Get the visible pills (current pair)
    const visiblePills = useMemo(() => {
        if (messages.length === 0)
            return [];
        if (messages.length <= pillsToShow)
            return messages;
        const pills = [];
        for (let i = 0; i < pillsToShow; i++) {
            pills.push(messages[(startIndex + i) % messages.length]);
        }
        return pills;
    }, [messages, startIndex, pillsToShow]);
    // Progressive disclosure - delayed appearance (#1)
    useEffect(() => {
        if (messages.length === 0 || !showPills)
            return;
        const timer = setTimeout(() => {
            setIsVisible(true);
            setAnimationClass('suggested-pill-enter');
        }, INITIAL_APPEARANCE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [messages.length, showPills]);
    // Rotation logic with hover pause (#5)
    useEffect(() => {
        if (messages.length <= pillsToShow || !isVisible || isHovered) {
            // Clear existing timer if hovered or not enough messages
            if (rotationTimerRef.current) {
                clearInterval(rotationTimerRef.current);
                rotationTimerRef.current = null;
            }
            return;
        }
        rotationTimerRef.current = setInterval(() => {
            setAnimationClass('suggested-pill-exit');
            setTimeout(() => {
                setStartIndex((prev) => (prev + pillsToShow) % messages.length);
                setAnimationClass('suggested-pill-enter');
            }, 300);
        }, rotationIntervalMs);
        return () => {
            if (rotationTimerRef.current) {
                clearInterval(rotationTimerRef.current);
                rotationTimerRef.current = null;
            }
        };
    }, [messages.length, rotationIntervalMs, isVisible, isHovered, pillsToShow]);
    // Reset index when messages change
    useEffect(() => {
        setStartIndex(0);
        if (isVisible) {
            setAnimationClass('suggested-pill-enter');
        }
    }, [messages, isVisible]);
    const handlePillClick = useCallback((message, localIndex) => {
        const globalIndex = (startIndex + localIndex) % messages.length;
        onPillClick(message, globalIndex);
    }, [startIndex, messages.length, onPillClick]);
    // Handle keyboard navigation (#8)
    const handleKeyDown = useCallback((e, message, localIndex) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handlePillClick(message, localIndex);
        }
    }, [handlePillClick]);
    // Don't render if no messages, pills disabled, dismissed, or not yet visible (progressive disclosure)
    if (!showPills || messages.length === 0 || !isVisible || isDismissed) {
        return null;
    }
    // Detect mobile for touch-friendly sizing (#6)
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    return (React__default.createElement("div", { style: Object.assign(Object.assign({}, getPillPosition(position, buttonSize, horizontalOffset, verticalOffset)), { zIndex: 2147483646, pointerEvents: 'auto', display: 'flex', flexDirection: 'column', alignItems: position.endsWith('right') ? 'flex-end' : 'flex-start', gap: isMobile ? '10px' : '8px' }), onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), "aria-live": "polite", "aria-atomic": "false", role: "region", "aria-label": "Suggested questions" },
        React__default.createElement("div", { style: {
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                paddingLeft: '4px',
                paddingRight: '4px',
            } },
            React__default.createElement("span", { style: {
                    fontSize: isMobile ? `${widgetConfig.pillHeaderFontSize + 1}px` : `${widgetConfig.pillHeaderFontSize}px`,
                    fontWeight: 600,
                    color: widgetConfig.pillHeaderColor,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                } }, headerText),
            React__default.createElement("button", { onClick: handleDismiss, style: {
                    background: 'none',
                    border: 'none',
                    padding: '2px',
                    cursor: 'pointer',
                    color: '#9ca3af',
                    fontSize: isMobile ? '14px' : '12px',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '2px',
                    transition: 'color 150ms ease',
                }, onMouseEnter: (e) => {
                    e.currentTarget.style.color = '#6b7280';
                }, onMouseLeave: (e) => {
                    e.currentTarget.style.color = '#9ca3af';
                }, "aria-label": "Dismiss suggested questions", title: "Dismiss" }, "\u00D7")),
        visiblePills.map((message, index) => (React__default.createElement("button", { key: `${startIndex}-${index}`, onClick: () => handlePillClick(message, index), onKeyDown: (e) => handleKeyDown(e, message, index), className: animationClass, tabIndex: 0, style: {
                // Touch-friendly sizing (#6)
                padding: isMobile
                    ? `${widgetConfig.pillPaddingY + 4}px ${widgetConfig.pillPaddingX + 2}px`
                    : `${widgetConfig.pillPaddingY}px ${widgetConfig.pillPaddingX}px`,
                minHeight: isMobile ? '48px' : '40px',
                backgroundColor: widgetConfig.pillBackgroundColor,
                borderRadius: `${widgetConfig.pillBorderRadius}px`,
                boxShadow: `0 2px 8px rgba(0, 0, 0, ${widgetConfig.pillShadowOpacity})`,
                fontSize: isMobile ? `${widgetConfig.pillFontSize + 1}px` : `${widgetConfig.pillFontSize}px`,
                fontWeight: 500,
                color: widgetConfig.pillTextColor,
                cursor: 'pointer',
                border: '2px solid transparent',
                outline: 'none',
                transition: 'all 150ms ease',
                maxWidth: isMobile ? '320px' : '280px',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
            }, onMouseEnter: (e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 4px 12px rgba(0, 0, 0, ${widgetConfig.pillShadowOpacity + 0.03})`;
            }, onMouseLeave: (e) => {
                e.currentTarget.style.backgroundColor = widgetConfig.pillBackgroundColor;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 2px 8px rgba(0, 0, 0, ${widgetConfig.pillShadowOpacity})`;
            }, onFocus: (e) => {
                e.currentTarget.style.borderColor = '#4f46e5';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.2)';
            }, onBlur: (e) => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.boxShadow = `0 2px 8px rgba(0, 0, 0, ${widgetConfig.pillShadowOpacity})`;
            }, "aria-label": `Ask: ${message}` },
            React__default.createElement("span", { style: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0, // Allow shrinking inside flex so ellipsis works
                } }, message))))));
};

export { SuggestedMessagePills };
//# sourceMappingURL=SuggestedMessagePills.js.map
